import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Grid,
  Paper
} from '@mui/material'; // added Paper
import axios from 'axios';
import { toast } from 'react-toastify';
import ImageCard from './ImageCard';

const ShopImageModal = ({
  open,
  onClose,
  product_id: productId,
  woocommerce_product_id
}) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  const fetchExistingImages = useCallback(async () => {
    try {
      const res = await axios.get(`/api/v1/shop-images/get-shop-images/${productId}`);
      setExistingImages(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch existing images');
    }
  }, [productId]);

  useEffect(() => {
    if (open) {
      setSelectedImages([]);
      setExistingImages([]);
      if (productId) fetchExistingImages();
    }
  }, [open, productId, fetchExistingImages]);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      alt_text: '',
      description: '',
      caption: '',
      tag: '',
      is_primary: true
    }));
    setSelectedImages((prev) => [...prev, ...newImages]);
  };

  const handleRemoveImage = async (index, isExisting) => {
    if (isExisting) {
      const imageId = existingImages[index].id;
      try {
        await axios.delete(`/api/v1/shop-images/delete-shop-image/${imageId}`);
        toast.success('Image deleted successfully');
        setExistingImages((prev) => prev.filter((_, i) => i !== index));
      } catch (err) {
        toast.error('Failed to delete image');
      }
    } else {
      setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleFieldChange = (index, field, value, isExisting) => {
    if (isExisting) {
      setExistingImages((prev) =>
        prev.map((img, i) => (i === index ? { ...img, [field]: value } : img))
      );
    } else {
      setSelectedImages((prev) =>
        prev.map((img, i) => (i === index ? { ...img, [field]: value } : img))
      );
    }
  };

  const handleUpload = async () => {
    if (!selectedImages.length) return;

    try {
      for (const imageData of selectedImages) {
        const formData = new FormData();
        formData.append('productId', productId);
        formData.append('alt_text', imageData.alt_text);
        formData.append('description', imageData.description);
        formData.append('caption', imageData.caption);
        formData.append('tag', imageData.tag);
        formData.append('is_primary', imageData.is_primary);
        formData.append('images', imageData.file);

        await axios.post('/api/v1/shop-images/upload-shop-images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        toast.success(`Uploaded ${imageData.file.name} successfully!`);
      }

      setSelectedImages([]);
      await fetchExistingImages();
    } catch (error) {
      toast.error(`Upload failed: ${error.message}`);
    }
  };

  const handleSync = async (imgData) => {
    const fullImageUrl = `https://dashboard.exhibitorsdata.com${imgData.imageUrl}`;

    const payload = {
      productId: Number(woocommerce_product_id),
      imageUrl: fullImageUrl,
      altText: imgData.alt_text || 'a',
      title: imgData.description || 't',
      caption: imgData.caption || 'c'
    };

    try {
      const res = await axios.post('/api/v1/shop-images/upload-shop-image-to-woocommerce', payload);

      if (res.status === 200) {
        toast.success(`✅ Image synced successfully: ${imgData.alt_text || imgData.imageUrl}`);
      } else {
        toast.error(`❌ Sync failed: ${res.data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Sync error:', err);
      toast.error(`❌ Sync failed: ${err.response?.data?.message || err.message}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#1976d2' }}>
        Manage Shop Images
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#f9f9f9' }}>
        <Box mb={2}>
          <Button
            variant="outlined"
            component="label"
            sx={{
              borderStyle: 'dashed',
              padding: '10px 20px',
              borderColor: '#1976d2',
              color: '#1976d2',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgb(0 0 0 / 0.1)',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1565c0',
                boxShadow:
                  '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                transform: 'scale(1.03)',
              },
            }}
          >
            📁 Select Images
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleImageSelect}
            />
          </Button>
        </Box>

        <Grid container spacing={3}>
          {existingImages.map((imgData, idx) => (
            <Grid item xs={12} sm={6} md={4} key={`existing-${imgData.id}`}>
              <Paper
                elevation={3}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  boxShadow:
                    '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  '&:hover': {
                    boxShadow:
                      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <ImageCard
                  imgData={imgData}
                  index={idx}
                  onFieldChange={handleFieldChange}
                  onRemove={handleRemoveImage}
                  isExisting
                  baseUrl="https://dashboard.exhibitorsdata.com"
                  handleSync={handleSync}
                />
              </Paper>
            </Grid>
          ))}

          {selectedImages.map((imgData, idx) => (
            <Grid item xs={12} sm={6} md={4} key={`new-${idx}`}>
              <Paper
                elevation={3}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  boxShadow:
                    '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  '&:hover': {
                    boxShadow:
                      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <ImageCard
                  imgData={imgData}
                  index={idx}
                  onFieldChange={handleFieldChange}
                  onRemove={handleRemoveImage}
                  isExisting={false}
                  handleSync={handleSync}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedImages.length}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: '#1976d2',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgb(0 0 0 / 0.1)',
              '&:hover': {
                backgroundColor: '#115293',
                boxShadow:
                  '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                transform: 'scale(1.05)',
              },
            }}
          >
            ⬆️ Upload All
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#333',
              borderColor: '#999',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgb(0 0 0 / 0.1)',
              '&:hover': {
                backgroundColor: '#eee',
                boxShadow:
                  '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                transform: 'scale(1.05)',
              },
            }}
          >
            ✖️ Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShopImageModal;
